import React from 'react';
import PropTypes from 'prop-types';
import { normalizeHref, uuid, validate, mergeStyles } from 'wix-rich-content-common';
import pluginGallerySchema from 'wix-rich-content-common/dist/statics/schemas/plugin-gallery.schema.json';
import { debounce, isEqual } from 'lodash';
import { GalleryOptions_Layout_Type } from 'ricos-schema';
import imageClientAPI from 'image-client-api/dist/imageClientSDK';
import classnames from 'classnames';
import { GALLERY_CONSTS, ProGallery } from 'pro-gallery';

const WIX_MEDIA_BASE_URL = "https://static.wixstatic.com/";
const WIX_VIDEO_BASE_URL = "https://video.wixstatic.com/";
const getWixFilename = (url) => url.replace(WIX_MEDIA_BASE_URL, "");
const isAbsoluteUrl = (url) => url.startsWith("http://") || url.startsWith("https://");
const getAbsoluteUrl = (url, type) => {
  if (isAbsoluteUrl(url)) {
    return url;
  }
  let baseUrl, baseFolder;
  if (type === "image") {
    baseUrl = WIX_MEDIA_BASE_URL;
    baseFolder = "media/";
  } else {
    baseUrl = WIX_VIDEO_BASE_URL;
    baseFolder = "video/";
  }
  const pathWithFolder = url.startsWith(baseFolder) ? url : baseFolder + url;
  return baseUrl + pathWithFolder;
};

var __defProp$4 = Object.defineProperty;
var __defProps$1 = Object.defineProperties;
var __getOwnPropDescs$1 = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$4 = Object.getOwnPropertySymbols;
var __hasOwnProp$4 = Object.prototype.hasOwnProperty;
var __propIsEnum$4 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$4 = (obj, key, value) => key in obj ? __defProp$4(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$4 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$4.call(b, prop))
      __defNormalProp$4(a, prop, b[prop]);
  if (__getOwnPropSymbols$4)
    for (var prop of __getOwnPropSymbols$4(b)) {
      if (__propIsEnum$4.call(b, prop))
        __defNormalProp$4(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$1 = (a, b) => __defProps$1(a, __getOwnPropDescs$1(b));
const convertItemData = ({ items, anchorTarget, relValue }) => items.map((item, index) => {
  const { metadata, metaData } = item;
  if (metaData && item.mediaUrl) {
    return item;
  }
  const convertedData = {
    mediaUrl: item.url,
    metaData: {
      link: {
        type: "none",
        target: "_blank"
      }
    },
    directLink: {}
  };
  if (metadata) {
    convertedData.metaData = __spreadProps$1(__spreadValues$4({}, metadata), {
      link: {
        type: "none",
        target: "_blank"
      }
    });
    if (item.metadata.type === "video") {
      convertedData.metaData.link = {
        target: "_blank",
        rel: "noopener",
        url: normalizeHref(item.url || "")
      };
      const { pathname, thumbnail: { pathname: thumbPathname, width, height } = {} } = convertedData.metaData.poster || {};
      if (pathname && thumbPathname) {
        convertedData.metaData.poster = {
          url: getAbsoluteUrl(thumbPathname, "image"),
          width,
          height
        };
      } else if (typeof convertedData.metaData.poster === "string") {
        convertedData.metaData.poster = {
          url: convertedData.metaData.poster,
          width: convertedData.metaData.width,
          height: convertedData.metaData.height
        };
      }
    }
    convertedData.metaData.alt = metadata.altText;
    if (item.metadata.link) {
      convertedData.metaData.link = {
        type: "wix",
        target: item.metadata.link.target || anchorTarget || "_self",
        data: {
          type: "ExternalLink",
          target: item.metadata.link.target || anchorTarget || "_self",
          rel: item.metadata.link.rel || relValue || "noopener",
          url: normalizeHref(item.metadata.link.url || "")
        }
      };
      convertedData.directLink = {
        url: normalizeHref(item.metadata.link.url || ""),
        target: item.metadata.link.target || anchorTarget || "_self",
        rel: item.metadata.link.rel || relValue || "noopener"
      };
    }
  }
  uuid();
  return __spreadProps$1(__spreadValues$4(__spreadProps$1(__spreadValues$4({}, item), {
    metadata: void 0
  }), convertedData), {
    // uuid is used to guarentee there are no 2 gallery items on the DOM with the same ID (on different galleries) due to a pro gallery issues with videos - it gives console errors and might also break the video controls behavoiur.
    // TODO: remove uuid when gallery fixes the issue \ release version 4.
    itemId: uuid() + "_" + index.toString(),
    hash: item.url + "_" + index.toString()
  });
});

const layoutData = {
  [GalleryOptions_Layout_Type.COLLAGE]: {
    isVertical: true,
    oneRow: false,
    galleryType: "Columns",
    gallerySizePx: "300"
  },
  [GalleryOptions_Layout_Type.MASONRY]: {
    numberOfImagesPerRow: 0,
    isVertical: false,
    gallerySizePx: "300"
  },
  [GalleryOptions_Layout_Type.GRID]: {
    numberOfImagesPerRow: 3,
    cubeType: "fill",
    cubeRatio: 1,
    isVertical: true,
    oneRow: false
  },
  [GalleryOptions_Layout_Type.THUMBNAIL]: {
    galleryThumbnailsAlignment: "bottom",
    thumbnailSpacings: 5
  },
  [GalleryOptions_Layout_Type.SLIDER]: {
    cubeType: "fit",
    cubeRatio: "16/9"
  },
  [GalleryOptions_Layout_Type.SLIDESHOW]: {
    cubeType: "fit"
  },
  [GalleryOptions_Layout_Type.PANORAMA]: {
    hasThumbnails: false,
    galleryThumbnailsAlignment: "none"
  },
  [GalleryOptions_Layout_Type.COLUMN]: {
    hasThumbnails: false,
    galleryThumbnailsAlignment: "none"
  },
  [GalleryOptions_Layout_Type.FULLSIZE]: {
    cubeType: "fill"
  }
};
({
  [GalleryOptions_Layout_Type.COLLAGE]: {
    layout: {
      type: "COLLAGE",
      orientation: "COLUMNS",
      horizontalScroll: false
    },
    item: {
      crop: "FILL",
      targetSize: 300
    },
    thumbnails: {
      placement: "NONE"
    }
  },
  [GalleryOptions_Layout_Type.MASONRY]: {
    layout: {
      type: "MASONRY",
      orientation: "ROWS",
      numberOfColumns: 0
    },
    item: {
      crop: "FILL",
      targetSize: 300
    },
    thumbnails: {
      placement: "NONE"
    }
  },
  [GalleryOptions_Layout_Type.GRID]: {
    layout: {
      type: "GRID",
      orientation: "COLUMNS",
      horizontalScroll: false,
      numberOfColumns: 3
    },
    item: {
      crop: "FILL",
      ratio: 1
    },
    thumbnails: {
      placement: "NONE"
    }
  },
  [GalleryOptions_Layout_Type.THUMBNAIL]: {
    layout: {
      type: "THUMBNAIL",
      orientation: "ROWS"
    },
    item: {
      crop: "FILL"
    },
    thumbnails: {
      placement: "BOTTOM",
      spacing: 5
    }
  },
  [GalleryOptions_Layout_Type.SLIDER]: {
    layout: {
      type: "SLIDER",
      orientation: "ROWS"
    },
    item: {
      crop: "FIT",
      ratio: 16 / 9
    },
    thumbnails: {
      placement: "NONE"
    }
  },
  [GalleryOptions_Layout_Type.SLIDESHOW]: {
    layout: {
      type: "SLIDESHOW",
      orientation: "ROWS"
    },
    item: {
      crop: "FILL"
    },
    thumbnails: {
      placement: "NONE"
    }
  },
  [GalleryOptions_Layout_Type.PANORAMA]: {
    layout: {
      type: "PANORAMA",
      orientation: "ROWS"
    },
    item: {
      crop: "FILL"
    },
    thumbnails: {
      placement: "NONE"
    }
  },
  [GalleryOptions_Layout_Type.COLUMN]: {
    layout: {
      type: "COLUMN",
      orientation: "ROWS"
    },
    item: {
      crop: "FILL"
    },
    thumbnails: {
      placement: "NONE"
    }
  },
  [GalleryOptions_Layout_Type.FULLSIZE]: {
    layout: {
      type: "FULLSIZE",
      orientation: "ROWS"
    },
    item: {
      crop: "FILL"
    },
    thumbnails: {
      placement: "NONE"
    }
  }
});

var __defProp$3 = Object.defineProperty;
var __getOwnPropSymbols$3 = Object.getOwnPropertySymbols;
var __hasOwnProp$3 = Object.prototype.hasOwnProperty;
var __propIsEnum$3 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$3 = (obj, key, value) => key in obj ? __defProp$3(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$3 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$3.call(b, prop))
      __defNormalProp$3(a, prop, b[prop]);
  if (__getOwnPropSymbols$3)
    for (var prop of __getOwnPropSymbols$3(b)) {
      if (__propIsEnum$3.call(b, prop))
        __defNormalProp$3(a, prop, b[prop]);
    }
  return a;
};
const GALLERY_ITEMS_TYPES = Object.freeze({
  IMAGE: "image",
  VIDEO: "video"
});
const HORIZONTAL_LAYOUTS = Object.freeze([
  GalleryOptions_Layout_Type.THUMBNAIL,
  GalleryOptions_Layout_Type.SLIDER,
  GalleryOptions_Layout_Type.SLIDESHOW,
  GalleryOptions_Layout_Type.COLUMN,
  GalleryOptions_Layout_Type.FULLSIZE
]);
const sampleItems = [1, 2, 3].map((i) => {
  return {
    metadata: {
      height: 10,
      width: 10
    },
    orderIndex: i,
    itemId: "sampleItem-" + i,
    url: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAABGdBTUEAALGPC/xhBQAAAAlwSFlzAAALEwAACxMBAJqcGAAAAVlpVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IlhNUCBDb3JlIDUuNC4wIj4KICAgPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4KICAgICAgPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIKICAgICAgICAgICAgeG1sbnM6dGlmZj0iaHR0cDovL25zLmFkb2JlLmNvbS90aWZmLzEuMC8iPgogICAgICAgICA8dGlmZjpPcmllbnRhdGlvbj4xPC90aWZmOk9yaWVudGF0aW9uPgogICAgICA8L3JkZjpEZXNjcmlwdGlvbj4KICAgPC9yZGY6UkRGPgo8L3g6eG1wbWV0YT4KTMInWQAAAA1JREFUCB1jePv27X8ACVkDxyMHIvwAAAAASUVORK5CYII="
    //eslint-disable-line
  };
});
const DEFAULTS = Object.freeze({
  items: [],
  styles: __spreadValues$3({
    galleryLayout: 2
  }, layoutData[GalleryOptions_Layout_Type.GRID]),
  config: {
    alignment: "center",
    size: "content"
  }
});
const LAYOUT_FIXED_STYLES = {
  [GalleryOptions_Layout_Type.COLLAGE]: {
    galleryType: "Columns",
    imageMargin: 5
  },
  [GalleryOptions_Layout_Type.MASONRY]: {
    imageMargin: 5,
    isVertical: false,
    gridStyle: 0
  },
  [GalleryOptions_Layout_Type.GRID]: {
    imageResize: false,
    gridStyle: 1,
    galleryImageRatio: 2,
    imageMargin: 5
  },
  [GalleryOptions_Layout_Type.THUMBNAIL]: {
    arrowsSize: 23,
    thumbnailSize: 120,
    floatingImages: 0
  },
  [GalleryOptions_Layout_Type.SLIDER]: {
    arrowsSize: 23,
    imageMargin: 5
  },
  [GalleryOptions_Layout_Type.SLIDESHOW]: {
    arrowsSize: 23,
    floatingImages: 0,
    slideshowInfoSize: 0
  },
  [GalleryOptions_Layout_Type.PANORAMA]: {
    hasThumbnails: false,
    imageMargin: 5
  },
  [GalleryOptions_Layout_Type.COLUMN]: {
    arrowsSize: 23,
    hasThumbnails: false,
    imageMargin: 5
  },
  [GalleryOptions_Layout_Type.FULLSIZE]: {
    arrowsSize: 23,
    floatingImages: 0,
    slideshowInfoSize: 0
  }
};
const FIXED_STYLES = {
  gallerySizeType: "px",
  enableInfiniteScroll: true,
  titlePlacement: "SHOW_ON_HOVER",
  hoveringBehaviour: "NO_CHANGE",
  alwaysShowHover: true,
  allowHover: true,
  itemClick: "link",
  fullscreen: false,
  loveButton: false,
  allowSocial: false,
  allowDownload: false,
  mobileSwipeAnimation: "NO_EFFECT",
  gotStyleParams: true,
  videoPlay: "onClick",
  videoLoop: false,
  videoSound: true,
  hidePlay: false,
  showVideoPlayButton: true,
  showVideoControls: true
};
const getFixedStyles = (layout) => {
  return __spreadValues$3(__spreadValues$3({}, FIXED_STYLES), LAYOUT_FIXED_STYLES[layout]);
};
const isHorizontalLayout = ({ galleryLayout, oneRow }) => HORIZONTAL_LAYOUTS.indexOf(galleryLayout) > -1 || oneRow;
const FROM_LAYOUT_NUMBER = {
  0: GalleryOptions_Layout_Type.COLLAGE,
  1: GalleryOptions_Layout_Type.MASONRY,
  2: GalleryOptions_Layout_Type.GRID,
  3: GalleryOptions_Layout_Type.THUMBNAIL,
  4: GalleryOptions_Layout_Type.SLIDER,
  5: GalleryOptions_Layout_Type.SLIDESHOW,
  6: GalleryOptions_Layout_Type.PANORAMA,
  7: GalleryOptions_Layout_Type.COLUMN,
  8: GalleryOptions_Layout_Type.MAGIC,
  9: GalleryOptions_Layout_Type.FULLSIZE
};

const getResizedImageUrl = (item, originalUrl, resizeMethod, requiredWidth, requiredHeight, sharpParams, focalPoint) => {
  originalUrl = originalUrl || "";
  sharpParams = sharpParams || {};
  if (sharpParams.quality > 0) {
    sharpParams.quality = Math.min(90, sharpParams.quality);
  }
  const focalPointObj = { x: 50, y: 50 };
  if (focalPoint && focalPoint[0] >= 0 && focalPoint[1] >= 0) {
    focalPointObj.x = Math.round(focalPoint[0] * 100);
    focalPointObj.y = Math.round(focalPoint[1] * 100);
  }
  if (sharpParams.allowUsm === true && sharpParams.usm) {
    sharpParams.usm.usm_a = Math.min(5, Math.max(0, sharpParams.usm.usm_a || 0));
    sharpParams.usm.usm_r = Math.min(128, Math.max(0, sharpParams.usm.usm_r || 0));
    sharpParams.usm.usm_t = Math.min(1, Math.max(0, sharpParams.usm.usm_t || 0));
  } else {
    sharpParams.usm = {
      usm_a: 0,
      usm_r: 0,
      usm_t: 0
    };
  }
  let resizer = () => {
  };
  if (resizeMethod === "fit") {
    resizer = imageClientAPI.getScaleToFitImageURL;
  } else {
    resizer = imageClientAPI.getScaleToFillImageURL;
  }
  const options = {};
  if (sharpParams.quality > 0) {
    options.quality = sharpParams.quality;
  }
  if (sharpParams.blur > 0) {
    options.filters = {
      blur: sharpParams.blur
    };
  }
  if (focalPointObj) {
    options.focalPoint = focalPointObj;
  }
  if (sharpParams && sharpParams.usm) {
    options.unsharpMask = {
      radius: parseFloat(sharpParams.usm.usm_r),
      amount: parseFloat(sharpParams.usm.usm_a),
      threshold: parseFloat(sharpParams.usm.usm_t)
    };
  }
  if (isAbsoluteUrl(originalUrl)) {
    return originalUrl;
  }
  return resizer(
    getWixFilename(originalUrl),
    item.maxWidth,
    item.maxHeight,
    requiredWidth,
    requiredHeight,
    options
  );
};
const resizeMediaUrl = ({
  item,
  originalUrl,
  resizeMethod,
  requiredWidth,
  requiredHeight,
  sharpParams,
  focalPoint
}) => {
  originalUrl = originalUrl || "";
  sharpParams = sharpParams || {};
  if (originalUrl.indexOf("base64") !== -1) {
    return originalUrl;
  }
  requiredWidth = Math.ceil(requiredWidth);
  requiredHeight = Math.ceil(requiredHeight);
  if (resizeMethod === GALLERY_ITEMS_TYPES.VIDEO) {
    return getAbsoluteUrl(originalUrl, GALLERY_ITEMS_TYPES.VIDEO);
  } else if (requiredWidth >= item.maxWidth && requiredHeight >= item.maxHeight) {
    const url = item.dto.metaData.type === GALLERY_ITEMS_TYPES.VIDEO ? originalUrl : item.url;
    return getAbsoluteUrl(url, GALLERY_ITEMS_TYPES.IMAGE);
  } else {
    return getResizedImageUrl(
      item,
      originalUrl,
      resizeMethod,
      requiredWidth,
      requiredHeight,
      sharpParams,
      focalPoint
    );
  }
};

var styles = {"gallery_container":"W4mqv","expandContainer":"MXyj8","itemOverlay":"SyS61","expandIcon":"Lj2Sg","clickableItem":"dJ6lh","imageTitle":"Hw7BS","imageTitleContainer":"XK-Zb","pointer":"KcqPO","fadeIn":"TC6p-","changing_background":"CcPWI"};

var __defProp$2 = Object.defineProperty;
var __getOwnPropSymbols$2 = Object.getOwnPropertySymbols;
var __hasOwnProp$2 = Object.prototype.hasOwnProperty;
var __propIsEnum$2 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$2 = (obj, key, value) => key in obj ? __defProp$2(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$2 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$2.call(b, prop))
      __defNormalProp$2(a, prop, b[prop]);
  if (__getOwnPropSymbols$2)
    for (var prop of __getOwnPropSymbols$2(b)) {
      if (__propIsEnum$2.call(b, prop))
        __defNormalProp$2(a, prop, b[prop]);
    }
  return a;
};
const expand = (props) => /* @__PURE__ */ React.createElement("svg", __spreadValues$2({ viewBox: "0 0 19 19", xmlns: "http://www.w3.org/2000/svg" }, props), /* @__PURE__ */ React.createElement(
  "path",
  {
    d: "M15.071 8.371V4.585l-4.355 4.356a.2.2 0 0 1-.283 0l-.374-.374a.2.2 0 0 1 0-.283l4.356-4.355h-3.786a.2.2 0 0 1-.2-.2V3.2c0-.11.09-.2.2-.2H16v5.371a.2.2 0 0 1-.2.2h-.529a.2.2 0 0 1-.2-.2zm-6.5 6.9v.529a.2.2 0 0 1-.2.2H3v-5.371c0-.11.09-.2.2-.2h.529c.11 0 .2.09.2.2v3.786l4.355-4.356a.2.2 0 0 1 .283 0l.374.374a.2.2 0 0 1 0 .283L4.585 15.07h3.786c.11 0 .2.09.2.2z",
    fill: "#000",
    fillRule: "nonzero"
  }
));

const GALLERY_TYPE = "wix-draft-plugin-gallery";

var __defProp$1 = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$1 = Object.getOwnPropertySymbols;
var __hasOwnProp$1 = Object.prototype.hasOwnProperty;
var __propIsEnum$1 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$1 = (obj, key, value) => key in obj ? __defProp$1(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$1 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$1.call(b, prop))
      __defNormalProp$1(a, prop, b[prop]);
  if (__getOwnPropSymbols$1)
    for (var prop of __getOwnPropSymbols$1(b)) {
      if (__propIsEnum$1.call(b, prop))
        __defNormalProp$1(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$1.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$1)
    for (var prop of __getOwnPropSymbols$1(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$1.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
var __publicField = (obj, key, value) => {
  __defNormalProp$1(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
const GALLERY_EVENTS = GALLERY_CONSTS.events;
class GalleryViewer extends React.Component {
  constructor(props) {
    validate(props.componentData, pluginGallerySchema);
    super(props);
    __publicField(this, "shouldUpdateDimensions", (prevComponentData) => {
      var _a, _b;
      const { galleryLayout: prevGalleryLayout } = (_a = prevComponentData.styles) != null ? _a : {};
      const { galleryLayout: currentGalleryLayout } = (_b = this.props.componentData.styles) != null ? _b : {};
      if (currentGalleryLayout !== prevGalleryLayout) {
        return true;
      }
      if (!isEqual(prevComponentData.config, this.props.componentData.config)) {
        return true;
      }
    });
    __publicField(this, "shouldConsiderThumbnailSize", () => {
      const { galleryLayout, galleryThumbnailsAlignment } = this.getComponentDataStyles();
      return this.props.isMobile && galleryLayout === 3 && ["top", "bottom"].includes(galleryThumbnailsAlignment);
    });
    __publicField(this, "getGalleryHeight", (width) => {
      let height = 305;
      if (width) {
        height = this.shouldConsiderThumbnailSize() ? Math.floor(width * 2 / 3) + 95 : Math.floor(width * 3 / 4);
      }
      return height;
    });
    __publicField(this, "getDimensions", () => {
      const width = Math.floor(this.containerRef.current.getBoundingClientRect().width);
      const height = this.getGalleryHeight(width);
      return { width, height };
    });
    __publicField(this, "updateDimensions", debounce(() => {
      var _a, _b;
      const { width, height } = this.getDimensions();
      if (width !== ((_a = this.state.size) == null ? void 0 : _a.width) || height !== ((_b = this.state.size) == null ? void 0 : _b.height)) {
        this.setState({ size: { width, height } });
      }
    }, 100));
    __publicField(this, "handleGalleryEvents", (name, data) => {
      const styleParams = this.getComponentDataStyles();
      switch (name) {
        case GALLERY_EVENTS.GALLERY_CHANGE:
          if (this.containerRef.current) {
            if (!isHorizontalLayout(styleParams)) {
              this.containerRef.current.style.height = `${data.layoutHeight}px`;
            } else {
              this.containerRef.current.style.height = "auto";
            }
          }
          break;
        case GALLERY_EVENTS.ITEM_ACTION_TRIGGERED:
          data.type !== "video" && !data.linkData.url && this.hasExpand() && this.handleExpand(data);
          break;
      }
    });
    __publicField(this, "handleExpand", (data) => {
      var _a;
      const {
        settings: { onExpand },
        helpers = {}
      } = this.props;
      (_a = helpers.onViewerAction) == null ? void 0 : _a.call(helpers, GALLERY_TYPE, "Click", "expand_gallery");
      this.hasExpand() && onExpand(this.props.blockKey, data.idx);
    });
    __publicField(this, "renderExpandIcon", (itemProps) => {
      return /* @__PURE__ */ React.createElement("div", { className: this.styles.expandContainer }, /* @__PURE__ */ React.createElement(
        expand,
        {
          className: this.styles.expandIcon,
          onClick: (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.handleExpand(itemProps);
          }
        }
      ));
    });
    __publicField(this, "hasExpand", () => {
      const { componentData, settings } = this.props;
      let disableExpand = false;
      if (componentData.disableExpand !== void 0) {
        disableExpand = componentData.disableExpand;
      } else if (settings.disableExpand !== void 0) {
        disableExpand = settings.disableExpand;
      }
      return !disableExpand && settings.onExpand;
    });
    __publicField(this, "renderTitle", (title) => {
      return title ? /* @__PURE__ */ React.createElement("div", { className: styles.imageTitleContainer }, /* @__PURE__ */ React.createElement("div", { className: this.styles.imageTitle }, title)) : null;
    });
    __publicField(this, "hoverElement", (itemProps) => {
      var _a, _b;
      const isClickable = this.hasExpand() || itemProps.link;
      const itemOverlayStyles = classnames(
        this.styles.itemOverlay,
        isClickable && this.styles.clickableItem
      );
      return /* @__PURE__ */ React.createElement("div", { className: itemOverlayStyles }, this.hasExpand() && this.renderExpandIcon(itemProps), this.renderTitle(itemProps.title, "HOVER"), (_b = (_a = this.props).itemOverlayElement) == null ? void 0 : _b.call(_a, itemProps));
    });
    __publicField(this, "getStyleParams", () => {
      const {
        componentData: { disableDownload },
        isMobile
      } = this.props;
      const styleParams = this.getComponentDataStyles();
      const calculatedStyles = __spreadValues$1(__spreadProps(__spreadValues$1({}, getFixedStyles(FROM_LAYOUT_NUMBER[styleParams.galleryLayout])), {
        allowContextMenu: !disableDownload,
        showArrows: isHorizontalLayout(styleParams)
      }), styleParams);
      if (isMobile) {
        if (calculatedStyles.m_numberOfImagesPerRow) {
          calculatedStyles.numberOfImagesPerRow = calculatedStyles.m_numberOfImagesPerRow;
        }
        if (isHorizontalLayout(calculatedStyles)) {
          calculatedStyles.arrowsSize = 20;
          !styleParams.imageMargin && (calculatedStyles.imageMargin = 0);
          if (calculatedStyles.galleryLayout === 3) {
            calculatedStyles.thumbnailSize = 90;
          }
        }
      }
      calculatedStyles.oneRow && (calculatedStyles.scrollDirection = GALLERY_CONSTS.scrollDirection.HORIZONTAL);
      calculatedStyles.thumbnailSpacings && (calculatedStyles.thumbnailSpacings /= 2);
      return calculatedStyles;
    });
    __publicField(this, "handleContextMenu", (e) => {
      const {
        componentData: { disableDownload = false }
      } = this.props;
      return disableDownload && e.preventDefault();
    });
    this.domId = this.props.blockKey || "v-" + this.props.entityIndex;
    this.containerRef = React.createRef();
    this.state = { items: [] };
  }
  componentDidMount() {
    window.addEventListener("resize", this.updateDimensions);
    this.setState({ size: this.getDimensions() });
    this.initUpdateDimensionsForDomChanges();
  }
  initUpdateDimensionsForDomChanges() {
    var _a;
    let { scrollingElement } = (_a = this.props) == null ? void 0 : _a.settings;
    if (!scrollingElement) {
      console.error(
        `Please fix the gallery config of Rich Content Editor.
        A scrollingElement needs to be provided. Without it the gallery will not work correctly`
      );
      scrollingElement = document.body;
    }
    let contentElement = typeof scrollingElement === "function" ? scrollingElement() : scrollingElement;
    if ((contentElement == null ? void 0 : contentElement.nodeType) !== 1) {
      contentElement = document.body;
    }
    if (contentElement) {
      this.observer = new MutationObserver(() => {
        if (contentElement.clientHeight !== this.oldContentElementHeight) {
          this.oldContentElementHeight = contentElement.clientHeight;
          this.updateDimensions();
        }
      });
      this.observer.observe(contentElement, { attributes: true, childList: true, subtree: true });
    } else {
      console.warn(`can't find content container to listen for changes to update gallery`);
    }
  }
  static getDerivedStateFromProps(props) {
    const {
      componentData: { items: contentItems },
      anchorTarget,
      relValue
    } = props;
    let items = contentItems || [];
    items = items.filter((item) => !item.error);
    items = items.length > 0 ? convertItemData({ items, anchorTarget, relValue }) : sampleItems;
    return {
      items
    };
  }
  getComponentDataStyles() {
    const { componentData } = this.props;
    return componentData.styles || __spreadValues$1({}, DEFAULTS);
  }
  shouldComponentUpdate(nextProps, nextState) {
    const {
      componentData: { styles: currentStyles, items: currentItems, config: currentConfig }
    } = this.props;
    const {
      componentData: { styles: nextStyles, items: nextItems, config: nextConfig }
    } = nextProps;
    return !isEqual(nextState.size, this.state.size) || !isEqual(nextStyles, currentStyles) || !isEqual(nextItems, currentItems) || !isEqual(nextConfig, currentConfig);
  }
  componentDidUpdate(prevProps) {
    if (this.shouldUpdateDimensions(prevProps.componentData)) {
      this.updateDimensions();
    }
  }
  componentWillUnmount() {
    this.observer.disconnect();
    window.removeEventListener("resize", this.updateDimensions);
    this.updateDimensions.cancel();
  }
  render() {
    const { theme, settings, seoMode } = this.props;
    this.styles = this.styles || mergeStyles({ styles, theme });
    const _a = settings, { scrollingElement } = _a, gallerySettings = __objRest(_a, ["scrollingElement"]);
    const { size, items } = this.state;
    const styleParams = this.getStyleParams();
    const viewMode = seoMode ? GALLERY_CONSTS.viewMode.SEO : void 0;
    return /* @__PURE__ */ React.createElement(
      "div",
      {
        ref: this.containerRef,
        className: this.styles.gallery_container,
        "data-hook": "galleryViewer",
        role: "none",
        onContextMenu: this.handleContextMenu
      },
      (size == null ? void 0 : size.width) ? /* @__PURE__ */ React.createElement(
        ProGallery,
        {
          domId: this.domId,
          id: this.domId,
          allowSSR: !!seoMode,
          items,
          options: styleParams,
          container: size,
          settings: gallerySettings,
          scrollingElement: typeof scrollingElement === "function" ? scrollingElement() : scrollingElement,
          eventsListener: this.handleGalleryEvents,
          createMediaUrl: resizeMediaUrl,
          viewMode,
          customComponents: {
            customHoverRenderer: this.hoverElement
          },
          totalItemsCount: items.length
        }
      ) : null
    );
  }
}
GalleryViewer.propTypes = {
  componentData: PropTypes.object.isRequired,
  blockKey: PropTypes.string,
  entityIndex: PropTypes.number,
  settings: PropTypes.object,
  theme: PropTypes.object.isRequired,
  isMobile: PropTypes.bool.isRequired,
  helpers: PropTypes.object.isRequired,
  anchorTarget: PropTypes.string.isRequired,
  relValue: PropTypes.string.isRequired,
  seoMode: PropTypes.bool,
  itemOverlayElement: PropTypes.elementType
};
var GalleryViewer$1 = GalleryViewer;

const typeMapper = () => ({
  [GALLERY_TYPE]: { component: GalleryViewer$1 }
});

var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
const pluginGallery = (config) => {
  return {
    config: __spreadValues(__spreadValues({}, DEFAULTS.config), config),
    type: GALLERY_TYPE,
    typeMapper
    // nodeViewRenderers,
  };
};

export { GALLERY_TYPE, typeMapper as galleryTypeMapper, pluginGallery };
//# sourceMappingURL=viewer.js.map

import 'wix-rich-content-plugin-gallery/dist/es/styles.viewer.min.global.css'